/**
 * Proof Validation Utility
 * Validates proof submissions against custom requirements
 * Requirements: Timestamp, GPS Location, Color Photo, ID Verification, Custom
 */

export interface ProofRequirement {
  type: 'timestamp' | 'gps' | 'color_photo' | 'id_verification' | 'multiple_photos' | 'video_proof' | 'audio_recording' | string;
  label: string;
  required: boolean;
}

export interface ValidationResult {
  valid: boolean;
  errors: string[];
  metadata: {
    hasTimestamp: boolean;
    hasGPS: boolean;
    isColor: boolean;
    hasMultiplePhotos: boolean;
    hasVideo: boolean;
    hasAudio: boolean;
    timestamp?: string;
    latitude?: number;
    longitude?: number;
  };
}

// EXIF tag mappings for different browsers
const EXIF_TAGS = {
  DateTime: 0x0132, // DateTimeOriginal
  DateTimeOriginal: 0x9003,
  GPSLatitude: 0x0002,
  GPSLongitude: 0x0004,
  GPSLatitudeRef: 0x0001,
  GPSLongitudeRef: 0x0003,
};

/**
 * Extract EXIF data from an image file
 */
async function extractExifData(file: File): Promise<any> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataView = new DataView(e.target?.result as ArrayBuffer);
      
      // Check for JPEG header
      if (dataView.getUint16(0) !== 0xFFD8) {
        resolve(null);
        return;
      }

      const exifData = parseExif(dataView, 2);
      resolve(exifData);
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file.slice(0, 128 * 1024)); // Read first 128KB for EXIF
  });
}

function parseExif(dataView: DataView, offset: number): any {
  const length = dataView.byteLength;
  const exif: any = {};

  while (offset < length) {
    if (dataView.getUint8(offset) !== 0xFF) break;
    
    const marker = dataView.getUint8(offset + 1);
    
    // APP1 marker (EXIF)
    if (marker === 0xE1) {
      const size = dataView.getUint16(offset + 2);
      const exifHeader = getString(dataView, offset + 4, 4);
      
      if (exifHeader === 'Exif') {
        const tiffOffset = offset + 10;
        parseTiff(dataView, tiffOffset, exif);
      }
      break;
    }
    
    if (marker === 0xD9 || marker === 0xDA) break; // EOI or SOS
    
    const segmentSize = dataView.getUint16(offset + 2);
    offset += 2 + segmentSize;
  }

  return exif;
}

function parseTiff(dataView: DataView, offset: number, exif: any): void {
  const byteOrder = dataView.getUint16(offset);
  const littleEndian = byteOrder === 0x4949;
  
  const ifdOffset = dataView.getUint32(offset + 4, littleEndian);
  parseIFD(dataView, offset + ifdOffset, littleEndian, exif, offset);
}

function parseIFD(dataView: DataView, offset: number, littleEndian: boolean, exif: any, tiffOffset: number): void {
  try {
    const numEntries = dataView.getUint16(offset, littleEndian);
    
    for (let i = 0; i < numEntries; i++) {
      const entryOffset = offset + 2 + (i * 12);
      if (entryOffset + 12 > dataView.byteLength) break;
      
      const tag = dataView.getUint16(entryOffset, littleEndian);
      const type = dataView.getUint16(entryOffset + 2, littleEndian);
      const numValues = dataView.getUint32(entryOffset + 4, littleEndian);
      
      // Check for GPS tags
      if (tag === 0x0001) exif.GPSLatitudeRef = getString(dataView, entryOffset + 8, 1);
      if (tag === 0x0002) {
        const lat = readRational(dataView, entryOffset + 8, littleEndian, tiffOffset);
        if (lat) exif.GPSLatitude = lat;
      }
      if (tag === 0x0003) exif.GPSLongitudeRef = getString(dataView, entryOffset + 8, 1);
      if (tag === 0x0004) {
        const lon = readRational(dataView, entryOffset + 8, littleEndian, tiffOffset);
        if (lon) exif.GPSLongitude = lon;
      }
      
      // DateTime tags
      if (tag === 0x0132) exif.DateTime = getString(dataView, entryOffset + 8, 19);
      if (tag === 0x9003) exif.DateTimeOriginal = getString(dataView, entryOffset + 8, 19);
    }
  } catch (e) {
    console.warn('EXIF parsing error:', e);
  }
}

function readRational(dataView: DataView, offset: number, littleEndian: boolean, baseOffset: number): number | null {
  try {
    if (offset + 8 > dataView.byteLength) return null;
    
    const num = dataView.getUint32(offset, littleEndian);
    const den = dataView.getUint32(offset + 4, littleEndian);
    
    if (den === 0) return null;
    
    // Handle offset format
    if (num > 4) {
      const actualOffset = num;
      const num2 = dataView.getUint32(actualOffset, littleEndian);
      const den2 = dataView.getUint32(actualOffset + 4, littleEndian);
      return num2 / den2;
    }
    
    return num / den;
  } catch {
    return null;
  }
}

function getString(dataView: DataView, offset: number, length: number): string {
  let str = '';
  try {
    for (let i = 0; i < length && offset + i < dataView.byteLength; i++) {
      const char = dataView.getUint8(offset + i);
      if (char === 0) break;
      str += String.fromCharCode(char);
    }
  } catch {}
  return str;
}

/**
 * Analyze image color to check if it's color or grayscale
 */
async function analyzeColor(file: File): Promise<{ isColor: boolean; isGrayscale: boolean }> {
  return new Promise((resolve) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    img.onload = () => {
      canvas.width = 100; // Small size for quick analysis
      canvas.height = 100;
      
      if (!ctx) {
        resolve({ isColor: true, isGrayscale: false }); // Assume color if can't analyze
        return;
      }
      
      ctx.drawImage(img, 0, 0, 100, 100);
      const imageData = ctx.getImageData(0, 0, 100, 100);
      const pixels = imageData.data;
      
      let totalColorVariation = 0;
      let grayscalePixels = 0;
      const sampleSize = pixels.length / 4;
      
      for (let i = 0; i < sampleSize; i++) {
        const r = pixels[i * 4];
        const g = pixels[i * 4 + 1];
        const b = pixels[i * 4 + 2];
        
        // Check if grayscale (R ≈ G ≈ B)
        const isGray = Math.abs(r - g) < 10 && Math.abs(g - b) < 10 && Math.abs(r - b) < 10;
        if (isGray) grayscalePixels++;
        
        // Calculate color variation
        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        const variation = max - min;
        totalColorVariation += variation;
      }
      
      const avgVariation = totalColorVariation / sampleSize;
      const grayPercentage = grayscalePixels / sampleSize;
      
      // If more than 90% pixels are grayscale and low variation, it's not a color photo
      const isGrayscale = grayPercentage > 0.9 && avgVariation < 30;
      const isColor = !isGrayscale && avgVariation > 40;
      
      resolve({ isColor, isGrayscale });
    };
    
    img.onerror = () => {
      resolve({ isColor: true, isGrayscale: false });
    };
    
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Validate proof against requirements
 */
export async function validateProofRequirements(
  files: File[],
  requirements: ProofRequirement[]
): Promise<ValidationResult> {
  const result: ValidationResult = {
    valid: true,
    errors: [],
    metadata: {
      hasTimestamp: false,
      hasGPS: false,
      isColor: true,
      hasMultiplePhotos: files.length >= 2,
      hasVideo: files.some(f => f.type.startsWith('video/')),
      hasAudio: files.some(f => f.type.startsWith('audio/')),
    },
  };

  const requiredRequirements = requirements.filter((r) => r.required);

  if (requiredRequirements.length === 0) {
    return result; // No requirements, always valid
  }

  // At least one image required for validation
  if (files.length === 0) {
    result.valid = false;
    result.errors.push('At least one image is required for validation');
    return result;
  }

  // Process each file for EXIF data
  const fileValidations = await Promise.all(
    files.map(async (file) => {
      const exif = await extractExifData(file);
      const colorAnalysis = await analyzeColor(file);
      
      let hasTimestamp = false;
      let hasGPS = false;
      let timestamp: string | undefined;
      let latitude: number | undefined;
      let longitude: number | undefined;
      
      if (exif) {
        // Check for timestamp
        if (exif.DateTimeOriginal || exif.DateTime) {
          hasTimestamp = true;
          timestamp = exif.DateTimeOriginal || exif.DateTime;
        }
        
        // Check for GPS
        if (exif.GPSLatitude && exif.GPSLongitude) {
          hasGPS = true;
          
          // Convert DMS to decimal
          const latRef = exif.GPSLatitudeRef === 'S' ? -1 : 1;
          const lonRef = exif.GPSLongitudeRef === 'W' ? -1 : 1;
          
          latitude = (exif.GPSLatitude as number) * latRef;
          longitude = (exif.GPSLongitude as number) * lonRef;
        }
      }
      
      return {
        hasTimestamp,
        hasGPS,
        isColor: colorAnalysis.isColor,
        timestamp,
        latitude,
        longitude,
      };
    })
  );

  // Merge results - use best values from all files
  const anyHasTimestamp = fileValidations.some((v) => v.hasTimestamp);
  const anyHasGPS = fileValidations.some((v) => v.hasGPS);
  const allIsColor = fileValidations.every((v) => v.isColor);

  result.metadata.hasTimestamp = anyHasTimestamp;
  result.metadata.hasGPS = anyHasGPS;
  result.metadata.isColor = allIsColor;

  // Check each requirement
  for (const req of requiredRequirements) {
    switch (req.type) {
      case 'timestamp':
        if (!anyHasTimestamp) {
          result.errors.push(`❌ Timestamp required: Photo must include date/time metadata`);
          result.valid = false;
        }
        break;
        
      case 'gps':
        if (!anyHasGPS) {
          result.errors.push(`❌ GPS Location required: Photo must include location metadata`);
          result.valid = false;
        }
        break;
        
      case 'color_photo':
        if (!allIsColor) {
          result.errors.push(`❌ Color Photo required: Photos must be in color, not grayscale`);
          result.valid = false;
        }
        break;
        
      case 'id_verification':
        // ID verification requires at least 2 photos: one for ID document, one for selfie
        if (files.length < 2) {
          result.errors.push(`❌ ID Verification requires at least 2 photos (ID document + selfie)`);
          result.valid = false;
        }
        break;
        
      case 'multiple_photos':
        if (files.length < 2) {
          result.errors.push(`❌ Multiple photos required: At least 2 photos must be submitted`);
          result.valid = false;
        }
        break;

      case 'video_proof':
        if (!result.metadata.hasVideo) {
          result.errors.push(`❌ Video proof required: Please upload a video file`);
          result.valid = false;
        }
        break;

      case 'audio_recording':
        if (!result.metadata.hasAudio) {
          result.errors.push(`❌ Audio recording required: Please upload an audio file`);
          result.valid = false;
        }
        break;

      default:
        // Custom requirements - accept as is for now
        break;
    }
  }

  return result;
}

/**
 * Format validation errors for display
 */
export function formatValidationErrors(errors: string[]): string {
  if (errors.length === 0) return '';
  
  return errors.map((err) => `• ${err}`).join('\n');
}

/**
 * Get requirement label for display
 */
export function getRequirementLabel(type: string): string {
  const labels: Record<string, string> = {
    timestamp: '⏰ Timestamp',
    gps: '📍 GPS Location',
    color_photo: '🎨 Color Photo',
    id_verification: '🪪 ID Verification',
    multiple_photos: '📸 Multiple Photos',
    video_proof: '🎥 Video Proof',
    audio_recording: '🎵 Audio Recording',
  };
  
  return labels[type] || type;
}